import { useEffect, useRef } from "react";

const DEFAULT_PLAYBACK_TIME = 5;

type UseTabAutoplayProps = {
  activeTab?: { index: number; playbackTimeInSeconds?: number };
  sections?: unknown[];
  /**
   * This allows to cancels out autoplay when user interacts with the carousel before the second slide has comes in
   * Should only be used in edge case situations where user actions cancels out autoplay at all
   * @default false
   */
  shouldCancelUpdate?: boolean;
  setActiveTab: (tab) => void;
};

type UseTabAutoplay = (props?: UseTabAutoplayProps) => {
  resetCurrentTimeout: (time: number) => void;
  destroyTimeout: () => void;
  timeout?: React.MutableRefObject<ReturnType<typeof setTimeout> | null>;
};

export const useTabAutoplay: UseTabAutoplay = (
  { activeTab, sections, shouldCancelUpdate, setActiveTab } = {
    activeTab: undefined,
    sections: [],
    shouldCancelUpdate: false,
    setActiveTab: undefined,
  },
) => {
  const timeout = useRef(null);

  const resetTimeout = () => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
  };

  useEffect(() => {
    if (activeTab == null || sections.length <= 1) {
      return;
    }

    resetTimeout();

    timeout.current = setTimeout(
      () => {
        if (shouldCancelUpdate) {
          return;
        }

        setActiveTab(({ index }) =>
          index === sections.length - 1 ? sections[0] : sections[index + 1],
        );
      },
      (activeTab?.playbackTimeInSeconds ?? DEFAULT_PLAYBACK_TIME) * 1000,
    );

    return () => resetTimeout();
  }, [activeTab, setActiveTab, shouldCancelUpdate, sections]);

  const resetCurrentTimeout = (time: number) => {
    resetTimeout();

    timeout.current = setTimeout(() => {
      setActiveTab(({ index }) =>
        index === sections.length - 1 ? sections[0] : sections[index + 1],
      );
    }, time * 1000);
  };

  const destroyTimeout = () => {
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }
  };

  return { resetCurrentTimeout, destroyTimeout, timeout };
};
